/**
 * Database Schema and Table Definitions
 *
 * NOTE: TypeScript types have been organized into domain-specific files in shared/types/
 * This file maintains backward compatibility by re-exporting all types from those files.
 *
 * For new imports, prefer importing from 'shared/types' or specific type files.
 * Example: import { User, Artist, Show } from 'shared/types';
 */

import { pgTable, text, varchar, serial, integer, boolean, decimal, timestamp, json, jsonb, bigint, date, time, uuid, unique } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Industry constants for consistent tagging
export const INDUSTRIES = [
  // Technology & Software
  "Software Development",
  "Technology Services",
  "Artificial Intelligence",
  "Cloud Computing",
  "Cybersecurity",
  "Data Analytics",
  "Mobile App Development",
  "SaaS",
  "Hardware Technology",
  "Fintech",
  
  // Finance & Professional Services
  "Banking & Financial Services",
  "Investment Management",
  "Insurance",
  "Real Estate",
  "Accounting",
  "Legal Services",
  "Consulting",
  "Business Services",
  
  // Healthcare & Life Sciences
  "Healthcare",
  "Pharmaceuticals",
  "Biotechnology",
  "Medical Devices",
  "Mental Health",
  "Telemedicine",
  
  // Consumer & Retail
  "E-commerce",
  "Retail",
  "Fashion & Apparel",
  "Beauty & Cosmetics",
  "Consumer Electronics",
  "Home & Garden",
  "Sports & Recreation",
  "Toys & Games",
  
  // Media & Entertainment
  "Media & Publishing",
  "Entertainment",
  "Gaming",
  "Music & Audio",
  "Film & Television",
  "Digital Content",
  "Social Media",
  "Streaming",
  
  // Food & Beverage
  "Food & Beverage",
  "Restaurants",
  "Food Delivery",
  "Alcohol & Spirits",
  "Coffee & Tea",
  "Organic & Health Foods",
  
  // Travel & Hospitality
  "Travel & Tourism",
  "Hotels & Lodging",
  "Airlines",
  "Car Rental",
  "Event Planning",
  
  // Automotive & Transportation
  "Automotive",
  "Electric Vehicles",
  "Rideshare & Transportation",
  "Logistics & Supply Chain",
  "Shipping & Delivery",
  
  // Energy & Environment
  "Energy & Utilities",
  "Renewable Energy",
  "Oil & Gas",
  "Environmental Services",
  "Clean Technology",
  
  // Education & Non-Profit
  "Education",
  "E-learning",
  "Non-Profit",
  "Government",
  "Religious Organizations",
  
  // Manufacturing & Industrial
  "Manufacturing",
  "Construction",
  "Agriculture",
  "Mining",
  "Aerospace",
  "Defense",
  
  // Other
  "Telecommunications",
  "Internet Services",
  "Advertising & Marketing",
  "Public Relations",
  "Other"
] as const;

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  userId: text("user_id"), // 7-digit alphanumeric ID - removed unique constraint temporarily
  email: text("email").notNull().unique("users_email_unique"),
  password: text("password").notNull(),
  role: text("role").notNull().default("guest"), // guest, subscriber, admin, super_admin
  adminLevel: text("admin_level").notNull().default("none"), // none, standard, admin, super_admin
  name: text("name").notNull(),
  company: text("company"),
  jobTitle: text("job_title"),
  phone: text("phone"),
  companyType: text("company_type"), // brand, agency
  industry: text("industry").array(),
  companyDescription: text("company_description"),
  companySize: text("company_size"),
  websiteUrl: text("website_url"),

  profileImageUrl: text("profile_image_url"),
  companyLogoUrl: text("company_logo_url"),
  subscriptionStatus: text("subscription_status").notNull().default("none"), // none, free, premium
  subscriptionDate: timestamp("subscription_date"),
  emailAlerts: boolean("email_alerts").default(false),
  emailShowUpdates: boolean("email_show_updates").default(false),
  emailArtistUpdates: boolean("email_artist_updates").default(false),
  emailMaxUpdates: boolean("email_max_updates").default(false),
  isActive: boolean("is_active").default(true),
  
  // Password reset fields
  resetToken: text("reset_token"),
  resetTokenExpires: timestamp("reset_token_expires"),
  resetAttempts: integer("reset_attempts").default(0),
  lastResetAttempt: timestamp("last_reset_attempt"),
  
  createdAt: timestamp("created_at").defaultNow(),
});

export const artists = pgTable("artists", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  maxArtistId: text("max_artist_id").unique("artists_max_artist_id_unique"), // External MAX artist ID
  genres: text("genres").array(),
  popularity: integer("popularity").default(0),
  imageUrl: text("image_url"),
  profileUrl: text("profile_url"),
  bio: text("bio"),
  
  // Career and location info
  careerStage: text("career_stage"), // Emerging, Breakthrough, Established, Mainstream
  city: text("city"),
  state: text("state"),
  country: text("country"),
  eras: text("eras"),
  
  // Fan demographics (JSONB arrays in database)
  fanGender: jsonb("fan_gender").$type<Array<{title: string, value: number}>>(),
  fanEthnicity: jsonb("fan_ethnicity").$type<Array<{title: string, value: number}>>(),
  fanAge: jsonb("fan_age").$type<Array<{title: string, value: number}>>(),
  fanIncome: jsonb("fan_income").$type<Array<{title: string, value: number}>>(),
  fanAttributes: jsonb("fan_attributes").$type<Array<{title: string, value: number}>>(),
  fanGeo: jsonb("fan_geo").$type<Array<{title: string, value: number, score?: number, audience_size?: number}>>(),
  interests: text("interests"),
  brandPreferences: text("brand_preferences"),
  brandExclusions: text("brand_exclusions"),
  
  // SET platform info
  setUser: text("set_user"),
  sponsorshipProducts: text("sponsorship_products"),
  
  // Social media URLs and metrics
  facebookUrl: text("facebook_url"),
  facebookFollowers: bigint("facebook_followers", { mode: "number" }),
  instagramUrl: text("instagram_url"),
  instagramFollowers: bigint("instagram_followers", { mode: "number" }),
  xUrl: text("x_url"),
  xFollowers: bigint("x_followers", { mode: "number" }),
  spotifyUrl: text("spotify_url"),
  spotifyArtistId: text("spotify_artist_id"), // Extracted from Spotify URL for API calls
  spotifyFollowers: bigint("spotify_followers", { mode: "number" }),
  spotifyMonthlyListeners: bigint("spotify_monthly_listeners", { mode: "number" }),
  youtubeUrl: text("youtube_url"),
  youtubeViews: bigint("youtube_views", { mode: "number" }),
  youtubeFollowers: bigint("youtube_followers", { mode: "number" }),
  tiktokUrl: text("tiktok_url"),
  tiktokFollowers: bigint("tiktok_followers", { mode: "number" }),

  // Social media engagement and growth metrics
  totalReach: bigint("total_reach", { mode: "number" }), // Sum of all platform followers
  avgEngagementRate: decimal("avg_engagement_rate", { precision: 5, scale: 4 }), // Average engagement across platforms
  trendingScore: decimal("trending_score", { precision: 8, scale: 2 }), // Calculated trending score
  
  // Platform-specific engagement rates
  instagramEngagementRate: decimal("instagram_engagement_rate", { precision: 5, scale: 4 }),
  tiktokEngagementRate: decimal("tiktok_engagement_rate", { precision: 5, scale: 4 }),
  youtubeEngagementRate: decimal("youtube_engagement_rate", { precision: 5, scale: 4 }),
  spotifyEngagementRate: decimal("spotify_engagement_rate", { precision: 5, scale: 4 }),
  facebookEngagementRate: decimal("facebook_engagement_rate", { precision: 5, scale: 4 }),
  xEngagementRate: decimal("x_engagement_rate", { precision: 5, scale: 4 }),
  
  // Platform-specific growth rates
  instagramGrowthRate: decimal("instagram_growth_rate", { precision: 5, scale: 4 }),
  tiktokGrowthRate: decimal("tiktok_growth_rate", { precision: 5, scale: 4 }),
  youtubeGrowthRate: decimal("youtube_growth_rate", { precision: 5, scale: 4 }),
  spotifyGrowthRate: decimal("spotify_growth_rate", { precision: 5, scale: 4 }),
  facebookGrowthRate: decimal("facebook_growth_rate", { precision: 5, scale: 4 }),
  xGrowthRate: decimal("x_growth_rate", { precision: 5, scale: 4 }),

  // Social media data last updated
  socialMediaLastUpdated: timestamp("social_media_last_updated"),

  // Legacy columns preserved to prevent data loss
  setArtistGroupId: text("set_artist_group_id"),
  vibeArtistUuid: text("vibe_artist_uuid"),
  vibeArtistSlug: text("vibe_artist_slug"),
  modifiedAt: timestamp("modified_at"),
  refreshedAt: timestamp("refreshed_at"),
  setScore: decimal("set_score"),
  extLinks: text("ext_links"),
  spotifyMonthlyStreams: bigint("spotify_monthly_streams", { mode: "number" }),

  createdAt: timestamp("created_at").defaultNow(),
});

export const venues = pgTable("venues", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  vibeVenueUuid: text("vibe_venue_uuid").unique("venues_vibe_venue_uuid_unique"), // External VIBE venue UUID
  imageUrl: text("image_url"),
  url: text("url"),
  capacity: integer("capacity"),
  streetAddress: text("street_address"),
  city: text("city"),
  state: text("state"),
  dmaId: integer("dma_id").references(() => dma.id),
  zipCode: text("zip_code"),
  country: text("country"),
  lat: decimal("lat", { precision: 10, scale: 8 }),
  lng: decimal("lng", { precision: 11, scale: 8 }),
  googlePlaceId: text("google_place_id"),
  
  venueType: text("venue_type"),

  // Legacy columns preserved to prevent data loss
  gmGlobalCode: text("gm_global_code"),
  extLinks: text("ext_links"),
  highlights: text("highlights"),
  setVenueId: text("set_venue_id"),
  vibeVenueSlug: text("vibe_venue_slug"),
  modifiedAt: timestamp("modified_at"),
  refreshedAt: timestamp("refreshed_at"),

  createdAt: timestamp("created_at").defaultNow(),
});

export const dma = pgTable("dma", {
  id: serial("id").primaryKey(),
  dmaCode: text("dma_code").notNull().unique("dma_dma_code_unique"),
  name: text("name").notNull(),
  majorCities: text("major_cities").array(),
  rank: integer("rank"), // DMA market rank
  zipCodes: text("zip_codes").array(), // Array of zip codes in this DMA
  states: text("states").array(), // Array of states covered by this DMA
  population: integer("population"), // Total population in DMA
  demographics: json("demographics").$type<{
    age?: { [key: string]: number };
    gender?: { [key: string]: number };
    income?: { [key: string]: number };
    ethnicity?: { [key: string]: number };
  }>(), // Demographic breakdown (JSONB for better performance)
  imageUrl: text("image_url"), // DMA market image
  marketSize: text("market_size"), // Small, Medium, Large based on population
  createdAt: timestamp("created_at").defaultNow(),
});

// Zip code to DMA lookup table (used by database trigger for venue DMA auto-population)
export const zipDma = pgTable("zip_dma", {
  zipCode: text("zip_code").primaryKey(),
  dmaCode: text("dma_code"),
  dmaDescription: text("dma_description"),
});

export const shows = pgTable("shows", {
  id: serial("id").primaryKey(),
  artistId: integer("artist_id").references(() => artists.id).notNull(),
  venueId: integer("venue_id").references(() => venues.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  modifiedAt: timestamp("modified_at"),
  refreshedAt: timestamp("refreshed_at"),
  date: date("date").notNull(),
  time: time("time"),
  sponsorable: boolean("sponsorable").default(true),
  priceTiers: json("price_tiers").$type<{
    premium: number;
    standard: number;
    basic: number;
  }>(),
  description: text("description"),
  setShowId: text("set_show_id").unique("shows_set_show_id_unique"),
  pollstarShowId: text("pollstar_show_id").unique("shows_pollstar_show_id_unique"),
  vibeShowUuid: uuid("vibe_show_uuid").unique("shows_vibe_show_id_unique"),
  vibeShowSlug: text("vibe_show_slug").unique("shows_vibe_slug_pk"),
}, (table) => ({
  artistVenueDateUnique: unique("shows_pk_artist_venue_date").on(table.artistId, table.venueId, table.date),
  artistVenueDateTimeUnique: unique("shows_pk_artist_venue_date_time").on(table.artistId, table.venueId, table.date, table.time),
}));

// Legacy lookup table mapping old career stage values to canonical ones
export const careerStageMappings = pgTable("career_stage_mappings", {
  id: serial("id").primaryKey(),
  legacyValue: text("legacy_value").notNull(),
  canonicalValue: text("canonical_value").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const plans = pgTable("plans", {
  id: serial("id").primaryKey(),
  buyerId: integer("buyer_id").references(() => users.id).notNull(),
  brandId: integer("brand_id").references(() => brands.id), // Reference to brand management system
  showIds: integer("show_ids").array().notNull(),
  campaignName: text("campaign_name").notNull(),
  brand: text("brand").notNull(),
  campaignGoals: text("campaign_goals"), // Marketing objective (awareness, engagement, leads, no_media)
  targetAudience: text("target_audience"),
  totalBudget: decimal("total_budget", { precision: 10, scale: 2 }).notNull(),

  // Enhanced proposal data
  numberOfShows: integer("number_of_shows").notNull(),
  estimatedCost: json("estimated_cost").$type<{ min: number; max: number }>(),
  careerStageBreakdown: json("career_stage_breakdown").$type<{
    emerging: number;
    breakthrough: number;
    established: number;
    mainstream: number;
  }>(),

  // Package tier information for each show
  showPackages: json("show_packages").$type<Array<{
    showId: number;
    packageTier: string;
    customPrice: number;
  }>>(),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const ideas = pgTable("ideas", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(), // Feature, Enhancement, Performance, UI/UX, Analytics
  priority: text("priority").notNull(), // Low, Medium, High, Critical
  status: text("status").notNull().default("Suggested"), // Suggested, In Progress, Completed
  dateAdded: text("date_added").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const brands = pgTable("brands", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  
  name: text("name").notNull(),
  description: text("description"),
  websiteUrl: text("website_url"),
  industry: text("industry").array(),
  companySize: text("company_size"),
  
  primaryContactName: text("primary_contact_name"),
  primaryContactEmail: text("primary_contact_email"),
  primaryContactPhone: text("primary_contact_phone"),
  
  logoUrl: text("logo_url"),
  brandColors: text("brand_colors").array(),
  brandGuidelinesUrl: text("brand_guidelines_url"),
  
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const brandAudienceProfiles = pgTable("brand_audience_profiles", {
  id: serial("id").primaryKey(),
  brandId: integer("brand_id").notNull(),
  profileName: text("profile_name").notNull(),
  description: text("description"),
  icon: text("icon"),

  // Demographic filters
  ageRanges: text("age_ranges").array(),
  genders: text("genders").array(),
  ethnicities: text("ethnicities").array(),
  incomeLevels: text("income_levels").array(),
  
  // Geographic targeting
  targetLocations: text("target_locations").array(),
  targetDmas: text("target_dmas").array(),
  
  // Interest and lifestyle
  interests: text("interests").array(),
  musicGenres: text("music_genres").array(),
  lifestyleAttributes: text("lifestyle_attributes").array(),
  
  // Engagement
  preferredChannels: text("preferred_channels").array(),
  engagementLevel: text("engagement_level"),
  
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const campaigns = pgTable("campaigns", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  brandId: integer("brand_id").notNull(),
  audienceProfileId: integer("audience_profile_id").notNull(),
  
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").default("draft"), // draft, active, paused, completed
  
  // Campaign details
  budgetRange: text("budget_range"), // $1K-$5K, $5K-$10K, etc.
  campaignObjective: text("campaign_objective"), // brand_awareness, conversions, engagement, etc.
  scope: text("scope"), // local, regional, national
  selectedDmas: integer("selected_dmas").array(), // Array of DMA IDs
  
  startDate: timestamp("start_date"),
  endDate: timestamp("end_date"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Quick personas for easy persona templates
export const quickPersonas = pgTable("quick_personas", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  icon: text("icon").notNull(),
  description: text("description"),
  
  // Demographic filters
  ageRanges: text("age_ranges").array(),
  genders: text("genders").array(),
  ethnicities: text("ethnicities").array(),
  incomeLevels: text("income_levels").array(),
  
  // Geographic targeting
  targetLocations: text("target_locations").array(),
  
  // Interest and lifestyle
  musicGenres: text("music_genres").array(),
  lifestyleAttributes: text("lifestyle_attributes").array(),
  
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});



// Removed legacy pricing tables:
// - artist_cost_pricing: deprecated flat pricing table
// - pricing_config: redundant with SET package tables (each has artist COGS and exponents)
// - package_config: redundant with SET package tables (each has package markup values)

// SET Core Pricing table with fixed prices per show count
export const setCorePricing = pgTable("pricing_set_core", {
  id: serial("id").primaryKey(),
  careerStage: text("career_stage").notNull().unique("pricing_set_core_career_stage_unique"),
  avgCapacity: integer("avg_capacity").notNull(),
  price1Show: integer("price_1_show").notNull(), // Price in cents for 1 show
  price2Shows: integer("price_2_shows").notNull(), // Price in cents per show for 2 shows
  price3Shows: integer("price_3_shows").notNull(), // Price in cents per show for 3 shows
  price4Shows: integer("price_4_shows").notNull(), // Price in cents per show for 4 shows
  price5PlusShows: integer("price_5_plus_shows").notNull(), // Price in cents per show for 5+ shows (min price)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// SET Audio Pricing table with formula-based pricing using fractional exponent
export const setAudioPricing = pgTable("pricing_set_audio", {
  id: serial("id").primaryKey(),
  careerStage: text("career_stage").notNull().unique("pricing_set_audio_career_stage_unique"),
  avgCapacity: integer("avg_capacity").notNull(),
  artistCogs: integer("artist_cogs").notNull(), // Artist COGS in cents
  exponent: decimal("exponent", { precision: 8, scale: 7 }).notNull(), // Volume exponent for sublinear curve
  packageType: text("package_type").notNull().default("SET Audio"), // Package type (SET Audio)
  packageMarkup: decimal("package_markup", { precision: 4, scale: 3 }).notNull(), // Package markup multiplier (e.g., 1.500)
  minCostPerShow: integer("min_cost_per_show").notNull(), // Minimum cost per show in cents
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// SET Video Pricing table with formula-based pricing using fractional exponent
export const setVideoPricing = pgTable("pricing_set_video", {
  id: serial("id").primaryKey(),
  careerStage: text("career_stage").notNull().unique("pricing_set_video_career_stage_unique"),
  avgCapacity: integer("avg_capacity").notNull(),
  artistCogs: integer("artist_cogs").notNull(), // Base artist COGS in cents
  exponent: decimal("exponent", { precision: 8, scale: 7 }).notNull(), // Volume exponent for sublinear curve
  packageType: text("package_type").notNull().default("SET Video"), // Package type (SET Video)
  packageMarkup: decimal("package_markup", { precision: 4, scale: 3 }).notNull(), // Package markup multiplier (2.875)
  minCostPerShow: integer("min_cost_per_show").notNull(), // Minimum cost per show in cents
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Career Stage Attendance table for calculating potential live attendees
export const careerStageAttendance = pgTable("career_stage_attendance", {
  id: serial("id").primaryKey(),
  careerStage: text("career_stage").notNull().unique("career_stage_attendance_career_stage_unique"), // Career stage name (e.g., "Developing", "Emerging", "Established 1")
  avgAttendees: integer("avg_attendees").notNull(), // Average attendees for this career stage
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Career Stages helper table for filter options with controlled sorting
export const careerStages = pgTable("career_stages", {
  id: serial("id").primaryKey(),
  careerStage: text("career_stage").notNull().unique("career_stages_career_stage_unique"),
  tierLevel: integer("tier_level").notNull(),
  sortOrder: integer("sort_order").notNull(),
  description: text("description"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type CareerStage = typeof careerStages.$inferSelect;
export type InsertCareerStage = typeof careerStages.$inferInsert;



// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  userId: true, // Generated automatically
  createdAt: true,
});

export const insertArtistSchema = createInsertSchema(artists).omit({
  id: true,
  createdAt: true,
});

export const insertVenueSchema = createInsertSchema(venues).omit({
  id: true,
  createdAt: true,
});

export const insertDmaSchema = createInsertSchema(dma).omit({
  id: true,
  createdAt: true,
});

export const insertShowSchema = createInsertSchema(shows).omit({
  id: true,
  createdAt: true,
});

export const insertPlanSchema = createInsertSchema(plans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertIdeaSchema = createInsertSchema(ideas).omit({
  id: true,
  createdAt: true,
});

export const insertQuickPersonaSchema = createInsertSchema(quickPersonas).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Removed artistCostPricing, pricingConfig, and packageConfig insert schemas

export const insertSetCorePricingSchema = createInsertSchema(setCorePricing).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSetAudioPricingSchema = createInsertSchema(setAudioPricing).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSetVideoPricingSchema = createInsertSchema(setVideoPricing).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCareerStageAttendanceSchema = createInsertSchema(careerStageAttendance).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertBrandSchema = createInsertSchema(brands).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertBrandAudienceProfileSchema = createInsertSchema(brandAudienceProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCampaignSchema = createInsertSchema(campaigns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ============================================================================
// TYPE RE-EXPORTS FOR BACKWARD COMPATIBILITY
// ============================================================================
// All types have been organized into domain-specific files in shared/types/
// The following re-exports maintain backward compatibility with existing code.
// ============================================================================

// User types
export type {
  User,
  InsertUser
} from "./types/users.types";

// Artist types
export type {
  Artist,
  InsertArtist,
  ArtistWithShows
} from "./types/artists.types";

// Venue and DMA types
export type {
  Venue,
  InsertVenue,
  Dma,
  InsertDma,
  VenueWithShows,
  MarketWithShows
} from "./types/venues.types";

// Show types
export type {
  Show,
  InsertShow,
  ShowWithDetails
} from "./types/shows.types";

// Plan types
export type {
  Plan,
  InsertPlan,
  PlanWithDetails
} from "./types/plans.types";

// Brand types
export type {
  Brand,
  InsertBrand,
  BrandAudienceProfile,
  InsertBrandAudienceProfile,
  BrandWithProfiles
} from "./types/brands.types";

// Campaign types
export type {
  Campaign,
  InsertCampaign,
  CampaignWithDetails
} from "./types/campaigns.types";

// Admin types
export type {
  QuickPersona,
  InsertQuickPersona,
  SetCorePricing,
  InsertSetCorePricing,
  SetAudioPricing,
  InsertSetAudioPricing,
  SetVideoPricing,
  InsertSetVideoPricing,
  CareerStageAttendance,
  InsertCareerStageAttendance,
  Idea,
  InsertIdea
} from "./types/admin.types";


// Common utility types
export type {
  PaginationOptions,
  PaginatedResult
} from "./types/common.types";

// Relations
export const usersRelations = relations(users, ({ many }) => ({
  plans: many(plans),
  brands: many(brands),
  campaigns: many(campaigns),
}));

export const artistsRelations = relations(artists, ({ many }) => ({
  shows: many(shows),
}));

export const venuesRelations = relations(venues, ({ one, many }) => ({
  dma: one(dma, {
    fields: [venues.dmaId],
    references: [dma.id],
  }),
  shows: many(shows),
}));

export const dmaRelations = relations(dma, ({ many }) => ({
  venues: many(venues),
}));

export const showsRelations = relations(shows, ({ one }) => ({
  artist: one(artists, {
    fields: [shows.artistId],
    references: [artists.id],
  }),
  venue: one(venues, {
    fields: [shows.venueId],
    references: [venues.id],
  }),
}));

export const plansRelations = relations(plans, ({ one }) => ({
  buyer: one(users, {
    fields: [plans.buyerId],
    references: [users.id],
  }),
}));

export const brandsRelations = relations(brands, ({ one, many }) => ({
  user: one(users, {
    fields: [brands.userId],
    references: [users.id],
  }),
  audienceProfiles: many(brandAudienceProfiles),
  campaigns: many(campaigns),
}));

export const brandAudienceProfilesRelations = relations(brandAudienceProfiles, ({ one, many }) => ({
  brand: one(brands, {
    fields: [brandAudienceProfiles.brandId],
    references: [brands.id],
  }),
  campaigns: many(campaigns),
}));


export const campaignsRelations = relations(campaigns, ({ one }) => ({
  user: one(users, {
    fields: [campaigns.userId],
    references: [users.id],
  }),
  brand: one(brands, {
    fields: [campaigns.brandId],
    references: [brands.id],
  }),
  audienceProfile: one(brandAudienceProfiles, {
    fields: [campaigns.audienceProfileId],
    references: [brandAudienceProfiles.id],
  }),
}));
